import React, { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import FieldSoilForm from "../FieldSettingForms/FieldSoilForm";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import { getUserId } from "../../utils/auth";

const FieldSoilFormWrapper = ({
  wizardData,
  completeStep,
  onPrevious,
  onNext,
  resetAllData,
  isWizardMode,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [soilData, setSoilData] = useState({
    zoneName: "",
    soilType: "",
    soiltype_id: "",  

    SoilProp: "Standard",
    RUmax: 100,
    effPluie: 80,
    irrigArea: "",
    ruPratique: 0.6,
    isStandard: true,
    fieldName: "",
    fieldId: "",
  });

  const [fields, setFields] = useState([]);
  const [listSoil, setListSoil] = useState([]);

  // Fonction pour gérer le changement de champ
  const handleFieldChange = (fieldId, fieldName) => {
    setSoilData((prev) => ({
      ...prev,
      fieldId: fieldId,
      fieldName: fieldName,
    }));
  };

  // Initialiser les données à partir du wizardData uniquement au montage
  useEffect(() => {
    if (wizardData?.step2?.data) {
      setSoilData({
        zoneName: wizardData.step2.data.zoneName || "",
        soilType: wizardData.step2.data.soilType || "",
              soiltype_id: wizardData.step2.data.soiltype_id || "",  

        SoilProp: wizardData.step2.data.SoilProp || "Standard",
        RUmax: wizardData.step2.data.RUmax || 100,
        effPluie: wizardData.step2.data.effPluie || 80,
        irrigArea: wizardData.step2.data.irrigArea || "",
        ruPratique: wizardData.step2.data.ruPratique || 0.6,
        isStandard: wizardData.step2.data.isStandard || true,
        fieldName: wizardData.fieldName || "",
        fieldId: wizardData.fieldId || "",
      });
    } else {
      // Initialiser avec les données du champ sélectionné
      setSoilData((prev) => ({
        ...prev,
        fieldName: wizardData.fieldName || "",
        fieldId: wizardData.fieldId || "",
      }));
    }
  }, []);

  useEffect(() => {
    const wasRecentlySubmitted = localStorage.getItem(
      "soilFormRecentlySubmitted"
    );
    const resetAllForms = localStorage.getItem("resetAllForms");

    if (wasRecentlySubmitted || resetAllForms) {
      // Réinitialiser le formulaire de sol
      setSoilData({
        zoneName: "",
        soilType: "",
        soiltype_id: "", 
        SoilProp: "Standard",
        RUmax: 100,
        effPluie: 80,
        irrigArea: "",
        ruPratique: 0.6,
        isStandard: true,
        fieldName: wizardData.fieldName || "",
        fieldId: wizardData.fieldId || "",
      });

      localStorage.removeItem("soilFormRecentlySubmitted");
      // Ne pas supprimer resetAllForms ici pour permettre aux autres composants de le lire
    }
  }, [wizardData.fieldName, wizardData.fieldId]);

  // Charger les données nécessaires
  useEffect(() => {
    const getSoils = async () => {
      try {
        const response = await api.get("/soils/get-soils");
        setListSoil(response.data.Soils || []);
      } catch (error) {
        console.error("Error loading soils:", error);
      }
    };

    const getFields = async () => {
      try {
        const response = await api.get("/field/fields");
        const fieldsList = [];
        response.data.farms.forEach((farm) => {
          if (farm.fields) {
            farm.fields.forEach((field) => {
              fieldsList.push({
                title: field.name,
                Uid: field.uid,
                Id: field.id,
              });
            });
          }
        });
        setFields(fieldsList);
      } catch (error) {
        console.error("Error loading fields:", error);
      }
    };

    getSoils();
    getFields();
  }, []);

  // Fonctions de mise à jour des données de sol
  const handleSoilType = (val) => {
    setSoilData((prev) => ({ ...prev, soilType: val }));
  };

  const handleRuMax = (val) => {
    setSoilData((prev) => ({ ...prev, RUmax: val }));
  };

  const handleEffRain = (val) => {
    setSoilData((prev) => ({ ...prev, effPluie: val }));
  };

  const handleRuPractical = (val) => {
    setSoilData((prev) => ({ ...prev, ruPratique: val }));
  };
  const handleIrrigArea = (val) => {
    setSoilData((prev) => ({ ...prev, irrigArea: val }));
  };

  // Gérer la soumission du formulaire

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Empêcher la double sauvegarde
    if (wizardData?.step2?.isCompleted) {
      onNext();
      return;
    }

    const apiData = {
      description: "",
      soilProperty: soilData.SoilProp,
      field_uid: soilData.fieldId,
      zone_uid: "",
      name: soilData.zoneName,
      source: "1",
      depth_data: [
        {
          uni: soilData.soilType,
          depth: "",
        },
      ],
      RUmax: soilData.RUmax,
      effPluie: soilData.effPluie,
      irrigArea: soilData.irrigArea,
      soiltype_id: soilData.soiltype_id,
    };

    try {
      const response = await api.post("/zone/add-zone", apiData);
      if (response.data.type === "success") {
        const soilFormData = {
          ...soilData,
          zoneId: response.data.zone.uid,
          zoneName: soilData.zoneName,
        };
        const isFirstSubmission = !wizardData?.step2?.isCompleted;
        localStorage.setItem("soilFormRecentlySubmitted", "true");
        completeStep(2, soilFormData);
        if (isFirstSubmission) {
          swal({
            title: t("Soil information added"),
            text: t("Would you like to continue to add crop information?"),
            icon: "success",
            buttons: [t("No"), t("Yes")],
          }).then((willContinue) => {
            if (willContinue) {
              onNext();
            } else {
              const userId = getUserId();
              localStorage.removeItem(`smartFarmWizardData_${userId}`);
              localStorage.setItem("resetAllForms", "true");
              localStorage.setItem("soilFormRecentlySubmitted", "true");
              navigate("/AddField");
            }
          });
        } else {
          onNext();
        }
      } else {
        swal(t("Error"), t("Failed to add soil information"), "error");
      }
    } catch (error) {
      swal(t("Error"), t("Failed to add soil information"), "error");
    }
  };
const handleSoilTypeId = (val) => {
  setSoilData(prev => ({...prev, soiltype_id: val}));
};
  // Mettre à jour les informations de zone
  const updateZoneInfo = (newZoneId, newZoneName) => {
    setSoilData((prev) => ({ ...prev, zoneName: newZoneName }));
  };

  return (
    <div className="wizard-step-content p-4">
      <Form onSubmit={handleSubmit}>
        <FieldSoilForm
          zoneName={soilData.zoneName}
          handleZoneName={(e) => {
            setSoilData({ ...soilData, zoneName: e.target.value });
          }}
          SoilProp={soilData.SoilProp}
          handleSoilProprety={(val) =>
            setSoilData({
              ...soilData,
              SoilProp: val,
              isStandard: val === "Standard",
            })
          }
          isStandard={soilData.isStandard}
          fields={fields}
          fieldName={soilData.fieldName}
          fieldId={soilData.fieldId}
          listSoils={listSoil}
          RUmax={soilData.RUmax}
          handleRuMax={handleRuMax}
          effPluie={soilData.effPluie}
          handleEffRain={handleEffRain}
          irrigArea={soilData.irrigArea}
          handleIrrigArea={handleIrrigArea}
          ruPratique={soilData.ruPratique}
          handleRuPractical={handleRuPractical}
          soilType={soilData.soilType}
          handleSoilType={handleSoilType}
          soiltype_id={soilData.soiltype_id}
          handleSoilTypeId={handleSoilTypeId}
          onUpdateZone={updateZoneInfo}
          handleFieldChange={handleFieldChange}
          isWizardMode={isWizardMode}
          showCarousel={true}
        />
        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="outline-secondary"
            onClick={onPrevious}
            className="bg-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            {t("previous")}
          </Button>
          <Button type="submit">
            {wizardData?.step2?.isCompleted ? (
              <>
                {t("next")}{" "}
                <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
              </>
            ) : (
              t("submit")
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default FieldSoilFormWrapper;
